import React, { useState } from 'react';
import { Modal, Form, Input, Upload, Button, message, Progress, Radio } from 'antd';
import { InboxOutlined, CopyOutlined, UploadOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://translation-manag-server.dev.torsunov.pro';

export function CreateTestLectureModal({ open, onCancel, onSuccess, testLecturesCount }) {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [sourceType, setSourceType] = useState('archive'); // 'archive' | 'copy'
    const [mediaId, setMediaId] = useState('');

    const defaultName = `Test lecture ${(testLecturesCount || 0) + 1}`;

    const hasArchive = fileList.length > 0;
    const hasMediaId = mediaId.trim().length > 0;
    const canSubmit = sourceType === 'archive' ? hasArchive : hasMediaId;

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        setUploadProgress(0);
        setSourceType('archive');
        setMediaId('');
        onCancel();
    };

    const handleSubmit = async () => {
        if (!canSubmit) {
            if (sourceType === 'archive') {
                message.error('Пожалуйста, добавьте архив с лекцией');
            } else {
                message.error('Пожалуйста, введите Media ID лекции');
            }
            return;
        }

        try {
            const values = await form.validateFields();
            setIsSubmitting(true);
            setUploadProgress(0);

            if (sourceType === 'copy') {
                // Режим копирования по Media ID — один запрос
                const response = await fetch(`${API_BASE_URL}/projects/test_lecture/create_from_copy`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        name: values.name || defaultName,
                        order_number: (testLecturesCount || 0) + 1,
                        source_media_id: mediaId.trim(),
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Ошибка при копировании лекции');
                }

                setUploadProgress(100);
                const createResult = await response.json();

                message.success('Тестовая лекция успешно создана из копии!');
                handleCancel();
                if (onSuccess) {
                    onSuccess(createResult);
                }
            } else {
                // Режим загрузки архива — текущая логика (два шага)

                // Шаг 1: Создаём пустой проект
                const createResponse = await fetch(`${API_BASE_URL}/projects/test_lecture/create`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        name: values.name || defaultName,
                        order_number: (testLecturesCount || 0) + 1
                    }),
                });

                if (!createResponse.ok) {
                    const errorData = await createResponse.json();
                    throw new Error(errorData.detail || 'Ошибка при создании проекта');
                }

                const createResult = await createResponse.json();
                const projectId = createResult.project_id;

                setUploadProgress(20);

                // Шаг 2: Загружаем архив
                const formData = new FormData();
                const fileToUpload = fileList[0].originFileObj || fileList[0];
                formData.append('file', fileToUpload);

                const uploadResponse = await fetch(
                    `${API_BASE_URL}/projects/test_lecture/${projectId}/upload`,
                    {
                        method: 'POST',
                        body: formData,
                    }
                );

                if (!uploadResponse.ok) {
                    const errorData = await uploadResponse.json();
                    throw new Error(errorData.detail || 'Ошибка при загрузке архива');
                }

                setUploadProgress(100);

                message.success('Тестовая лекция успешно создана!');
                handleCancel();
                if (onSuccess) {
                    onSuccess(createResult);
                }
            }
        } catch (error) {
            console.error('Error creating test lecture:', error);
            message.error(error.message || 'Не удалось создать тестовую лекцию');
        } finally {
            setIsSubmitting(false);
        }
    };

    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.zip',
        fileList,
        beforeUpload: (file) => {
            // Проверяем, что файл является ZIP-архивом
            if (!file.name.toLowerCase().endsWith('.zip')) {
                message.error('Можно загружать только ZIP-архивы');
                return Upload.LIST_IGNORE;
            }
            setFileList([file]);
            return false; // Отменяем автоматическую загрузку
        },
        onRemove: () => {
            setFileList([]);
        },
    };

    return (
        <Modal
            title="Создать тестовую лекцию"
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={500}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ name: defaultName }}
            >
                <Form.Item
                    name="name"
                    label="Название лекции"
                    rules={[{ required: true, message: 'Введите название лекции' }]}
                >
                    <Input placeholder={defaultName} />
                </Form.Item>

                <Form.Item label="Источник данных">
                    <Radio.Group
                        value={sourceType}
                        onChange={(e) => setSourceType(e.target.value)}
                        style={{ marginBottom: 16 }}
                    >
                        <Radio.Button value="archive">
                            <UploadOutlined style={{ marginRight: 4 }} />
                            Загрузить архив
                        </Radio.Button>
                        <Radio.Button value="copy">
                            <CopyOutlined style={{ marginRight: 4 }} />
                            Скопировать по Media ID
                        </Radio.Button>
                    </Radio.Group>
                </Form.Item>

                {sourceType === 'archive' ? (
                    <Form.Item
                        label="Архив с лекцией"
                        required
                    >
                        <Dragger {...uploadProps}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Нажмите или перетащите ZIP-архив сюда
                            </p>
                            <p className="ant-upload-hint">
                                Поддерживаются только ZIP-архивы
                            </p>
                        </Dragger>
                    </Form.Item>
                ) : (
                    <Form.Item
                        label="Media ID лекции"
                        required
                    >
                        <Input
                            placeholder="Введите Media ID существующей лекции"
                            value={mediaId}
                            onChange={(e) => setMediaId(e.target.value)}
                        />
                        <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                            Папка лекции будет полностью скопирована как новая тестовая лекция
                        </div>
                    </Form.Item>
                )}

                {isSubmitting && uploadProgress > 0 && (
                    <Form.Item>
                        <Progress percent={uploadProgress} status="active" />
                    </Form.Item>
                )}

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                        Отмена
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={!canSubmit}
                        style={{
                            backgroundColor: canSubmit ? '#fa8c16' : undefined,
                            borderColor: canSubmit ? '#fa8c16' : undefined,
                        }}
                    >
                        Создать
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
}
